import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, colors, WoltLogo } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';

import UnsavedChangesModal from './UnsavedChangesModal';
import { SsmoTask, SsmoStepsIdentifiers } from '../constants';
import { SelfServeStepType } from '../types';

export type Props = {
  title: string;
  step: SelfServeStepType;
  task: SsmoTask;
  children: React.ReactNode;
  disabled?: boolean;
};

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  flex-direction: column;
  align-items: center;
  background-color: ${cssThemedValue({ dark: cbSemanticCssVars.surfaceMain, light: cbSemanticCssVars.surfaceMain })};
`;

const HeaderWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  width: 100%;
  height: 64px;
  padding: 10px 24px;
  justify-content: space-between;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 480px;
  margin-block: 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-inline: 16px;
    width: 100%;
  }
`;

const StyledWoltLogo = styled(WoltLogo)`
  height: 16px;
  fill: ${cssThemedValue({ light: colors.pepper100, dark: colors.salt100 })};
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTitle = styled.div`
  color: ${cbSemanticCssVars.textPrimary};
  font-weight: bold;
`;

const StepPageWrapper = ({ title, children, disabled, step }: Props) => {
  const isMobile = useIsMobile();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const onExit = () => history.push('/onboarding-page');
  const noButton = step === SsmoStepsIdentifiers.SignContract;

  // TODO: fix in https://woltwide.atlassian.net/browse/MT-4316
  const renderCloseButton =
    step === SsmoStepsIdentifiers.ContractDetails ||
    step === SsmoStepsIdentifiers.KybDataSubmition ||
    step === SsmoStepsIdentifiers.KYC;

  return (
    <Root>
      <HeaderWrapper isMobile={isMobile}>
        <Left>
          <StyledWoltLogo />
          <StyledTitle>{title}</StyledTitle>
        </Left>

        {!noButton && (
          <Right>
            {renderCloseButton ? (
              <Button variant="inverse" size="small" onClick={onExit}>
                <StyledTitle>{formatMessage({ id: 'state.close' })}</StyledTitle>
              </Button>
            ) : (
              <Button variant="inverse" size="small" type="submit" form={step} onClick={() => disabled && onExit()}>
                <StyledTitle>
                  {formatMessage({ id: 'state.save' })} & {formatMessage({ id: 'state.close' })}
                </StyledTitle>
              </Button>
            )}
          </Right>
        )}
      </HeaderWrapper>
      <ContentWrapper>{children}</ContentWrapper>
      <UnsavedChangesModal isOpen={isModalOpen} onClose={onExit} onCancel={() => setIsModalOpen(false)} />
    </Root>
  );
};

export default StepPageWrapper;
